import CircleIcon from '@mui/icons-material/Circle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ListItem, ListItemIcon, ListItemText, Stack, Typography } from '@mui/material';
import { Routes } from 'enums';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';

interface Props {
  hasLinkedinAccounts?: boolean;

  handleCloseModal(): void;

  handleOpenAddLinkedinAccountDialog?(isStarterExperience: boolean): () => void;
}

interface ListItemProps {
  text: string | React.ReactNode;
}

const CustomListItem: React.FC<ListItemProps> = ({ text }) => {
  const { classes } = useStyles();

  return (
    <ListItem className={classes.listItem}>
      <ListItemIcon className={classes.listItemIcon}>
        <CircleIcon className={classes.listItemIconDot} />
      </ListItemIcon>
      <ListItemText className={classes.listItemText} primary={text} />
    </ListItem>
  );
};

const FirstSteps: React.FC<Props> = ({ hasLinkedinAccounts, handleCloseModal, handleOpenAddLinkedinAccountDialog }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();

  const product = location.pathname.includes(Routes.RECRUITING_BOOSTER_CO_PILOT)
    ? 'hr-booster'
    : location.pathname.includes(Routes.LEAD_BOOSTER_CO_PILOT)
    ? 'lead-booster'
    : undefined;

  const openBookMeeting = () => {
    window.open('https://meetings.hubspot.com/ralph-werner?uuid=89a0ef10-2f17-45f2-a469-f209f5ec7f52', '_blank');
  };

  const handleAddLinkedinAccountNow = () => {
    handleCloseModal();

    if (!_.isNil(handleOpenAddLinkedinAccountDialog)) {
      handleOpenAddLinkedinAccountDialog(true)();
    }
  };

  return (
    <>
      <img className={classes.image} src={'/dashboard/images/onboarding/onboarding_bg.png'} alt="onboarding" />
      <Stack className={classes.firstSteps} justifyContent={'center'} alignItems={'center'}>
        <Typography className={classes.title}>{t('yourFirstSteps')}</Typography>
        <Typography className={classes.subTitle}>{t('doThisNow')}</Typography>
        <div className={classes.card}>
          <Stack gap={2} mb={'32px'}>
            <CustomListItem text={t('addLinkedinAccount')} />
            <CustomListItem
              text={
                <Link className={classes.link} to={product ? `${Routes.UPGRADE}?product=${product}` : Routes.UPGRADE} target={'_blank'}>
                  <PaiLink
                    text={t('upgradeYourLicense')}
                    size={'lg'}
                    variant={'primary'}
                    underline={'always'}
                    IconComponent={<OpenInNewIcon />}
                    iconPlacement={'end'}
                  />
                </Link>
              }
            />
            <CustomListItem
              text={
                <PaiLink
                  text={t('bookAnOnboarding')}
                  size={'lg'}
                  variant={'primary'}
                  underline={'always'}
                  action={openBookMeeting}
                  IconComponent={<OpenInNewIcon />}
                  iconPlacement={'end'}
                />
              }
            />
          </Stack>
          <Stack gap={2}>
            <PaiButton text={t('addLinkedinAccountNow')} size={'medium'} border={'rounded'} fullWidth={true} action={handleAddLinkedinAccountNow} />
            {hasLinkedinAccounts ? (
              <Stack direction={'row'} justifyContent={'center'}>
                <PaiLink text={t('addLinkedinAccountLater')} underline={'always'} action={handleCloseModal} />
              </Stack>
            ) : null}
          </Stack>
        </div>
      </Stack>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  firstSteps: {
    position: 'absolute',
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%',
    minHeight: '600px',
    width: 'auto',
    height: 'auto',
  },
  title: {
    fontSize: theme.typography.pxToRem(24),
    fontWeight: 'bold',
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    marginBottom: '16px',
    maxWidth: '450px',
  },
  subTitle: {
    fontSize: theme.typography.pxToRem(18),
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    marginBottom: '32px',
  },
  card: {
    background: '#FFF',
    padding: '32px',
    width: '450px',
    boxShadow: Shadows.GREY_SHADOW_LIGHT,
    borderRadius: '10px',
  },
  listItem: {
    alignItems: 'flex-start',
    padding: 0,
    paddingLeft: 0,
  },
  listItemIcon: {
    color: theme.palette.primary.main,
    minWidth: 0,
    marginRight: '12px',
    marginTop: '6px',
  },
  listItemIconDot: {
    fontSize: theme.typography.pxToRem(11),
  },
  listItemText: {
    margin: 0,
    '& .MuiListItemText-primary': {
      fontSize: theme.typography.pxToRem(16),
    },
    '& .MuiListItemText-secondary': {
      fontSize: theme.typography.pxToRem(16),
      marginTop: '2px',
      lineHeight: 1.5,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
  },
  link: {
    textDecoration: 'none',
  },
}));

export default FirstSteps;
